import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useRegions } from '@/features/alojamientos/hooks/useRegions';
import { useSegments } from '@/features/alojamientos/hooks/useSegments';
import { DashboardSourceSelect } from './DashboardSourceSelect';
import { isOfficialSource } from '../utils/statsSource.utils';
import type { DashboardFilters, StatsSource } from '../types/dashboard.types';

interface DashboardFiltersProps {
  filters: DashboardFilters;
  onFiltersChange: (filters: DashboardFilters) => void;
  onExportData: () => void;
  onGenerateChart: () => void;
}

export const DashboardFiltersComponent: React.FC<DashboardFiltersProps> = ({
  filters,
  onFiltersChange,
  onExportData,
  onGenerateChart,
}) => {
  const { regions } = useRegions();
  const { segments } = useSegments();

  const handleRegionChange = (value: string) => {
    if (value === 'all') {
      onFiltersChange({ ...filters, region: undefined });
    } else {
      const regionId = parseInt(value);
      const currentRegions = filters.region || [];
      const newRegions = currentRegions.includes(regionId)
        ? currentRegions.filter(id => id !== regionId)
        : [...currentRegions, regionId];
      
      onFiltersChange({ 
        ...filters, 
        region: newRegions.length > 0 ? newRegions : undefined 
      });
    }
  };

  const handleSegmentChange = (value: string) => {
    if (value === 'all') {
      onFiltersChange({ ...filters, segment: undefined });
    } else {
      const currentSegments = filters.segment || [];
      const newSegments = currentSegments.includes(value)
        ? currentSegments.filter(seg => seg !== value)
        : [...currentSegments, value];
      
      onFiltersChange({ 
        ...filters, 
        segment: newSegments.length > 0 ? newSegments : undefined 
      });
    }
  };

  const removeRegionFilter = (regionId: number) => {
    const newRegions = (filters.region || []).filter(id => id !== regionId);
    onFiltersChange({ 
      ...filters, 
      region: newRegions.length > 0 ? newRegions : undefined 
    });
  };

  const removeSegmentFilter = (segment: string) => {
    const newSegments = (filters.segment || []).filter(seg => seg !== segment);
    onFiltersChange({ 
      ...filters, 
      segment: newSegments.length > 0 ? newSegments : undefined 
    });
  };

  const handleSourceChange = (source: StatsSource) => {
    onFiltersChange({ ...filters, source: source === 'official' ? undefined : source });
  };

  // The source is a view mode, not a filter: clearing filters keeps it
  const clearAllFilters = () => {
    onFiltersChange({ source: filters.source });
  };

  const segmentExcludesPreRegistrations = !isOfficialSource(filters.source) && !!filters.segment?.length;

  const hasActiveFilters = filters.region?.length || filters.segment?.length || filters.status?.length;

  return (
    <div className="space-y-4">
      {/* Main filters row - removed export/chart buttons since they're now in header */}
      <div className="flex flex-wrap items-center gap-4">
        <DashboardSourceSelect value={filters.source} onChange={handleSourceChange} />

        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700">Región:</label>
          <Select value="select" onValueChange={handleRegionChange}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Seleccionar región" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las regiones</SelectItem>
              {regions.map((region) => (
                <SelectItem key={region.id} value={region.id.toString()}>
                  {region.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700">Segmento:</label>
          <Select value="select" onValueChange={handleSegmentChange}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Seleccionar segmento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los segmentos</SelectItem>
              {segments.map((segment) => (
                <SelectItem key={segment.id} value={segment.id}>
                  {segment.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {hasActiveFilters && (
          <Button 
            onClick={clearAllFilters}
            variant="outline"
            size="sm"
            className="text-xs"
          >
            Limpiar filtros
          </Button>
        )}
      </div>

      {segmentExcludesPreRegistrations && (
        <p className="text-sm text-amber-700">
          Los preinscriptos no tienen segmento: con este filtro solo se cuentan alojamientos oficiales.
        </p>
      )}

      {/* Active Filters */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-600">Filtros activos:</span>
          
          {filters.region?.map((regionId) => {
            const region = regions.find(r => r.id === regionId);
            return region ? (
              <Badge key={regionId} variant="secondary" className="text-xs">
                {region.name}
                <button
                  onClick={() => removeRegionFilter(regionId)}
                  className="ml-1 hover:text-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ) : null;
          })}

          {filters.segment?.map((segment) => (
            <Badge key={segment} variant="secondary" className="text-xs">
              Segmento {segment}
              <button
                onClick={() => removeSegmentFilter(segment)}
                className="ml-1 hover:text-red-600"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};